import devLog from '../../Utils/logger';

/**
 * Manages typing indicator timers to automatically clear stuck "User is typing..." indicators.
 *
 * When a user starts typing, a 30-second timer is set. If the user doesn't send a stop-typing
 * event within that time, the timer automatically fires to clear the typing indicator.
 */
class TypingTimerManager {
  private timers: Map<string, NodeJS.Timeout>;
  private readonly timeoutDuration: number;

  constructor(timeoutDuration: number = 30000) {
    this.timers = new Map();
    this.timeoutDuration = timeoutDuration;
  }

  /**
   * Generates a unique key for a chatroom-user combination
   */
  private getTimerKey(chatroomId: number, userId: number): string {
    return `${chatroomId}-${userId}`;
  }

  /**
   * Starts a typing timer for a specific user in a chatroom.
   * If a timer already exists for this user/chatroom, it will be cleared and replaced.
   *
   * @param chatroomId - The ID of the chatroom
   * @param userId - The ID of the user who started typing
   * @param onTimeout - Callback to execute when the timer expires
   */
  startTimer(chatroomId: number, userId: number, onTimeout: () => void): void {
    const timerKey = this.getTimerKey(chatroomId, userId);

    // Clear any existing timer for this user/chatroom
    this.stopTimer(chatroomId, userId);

    // Set new timer
    const timer = setTimeout(() => {
      devLog('socket', ['user-typing-timeout', { chatroom_id: chatroomId, user_id: userId }]);
      onTimeout();
      this.timers.delete(timerKey);
    }, this.timeoutDuration);

    this.timers.set(timerKey, timer);
  }

  /**
   * Stops and clears a typing timer for a specific user in a chatroom.
   *
   * @param chatroomId - The ID of the chatroom
   * @param userId - The ID of the user who stopped typing
   * @returns true if a timer was cleared, false if no timer existed
   */
  stopTimer(chatroomId: number, userId: number): boolean {
    const timerKey = this.getTimerKey(chatroomId, userId);
    const existingTimer = this.timers.get(timerKey);

    if (existingTimer) {
      clearTimeout(existingTimer);
      this.timers.delete(timerKey);
      return true;
    }

    return false;
  }

  /**
   * Clears all active typing timers.
   * Should be called when disconnecting or cleaning up.
   */
  clearAllTimers(): void {
    this.timers.forEach((timer) => {
      clearTimeout(timer);
    });
    this.timers.clear();
  }
}

export default TypingTimerManager;
